import { service } from '@ember/service';

import type { Service } from 'webapp-types';
import { ModelName } from 'webapp/enums/enums';
import { getSourcesTexts } from 'webapp/utils/common';

import type { Report } from 'webapp/models-custom';
import ReportScheduleSetting from 'webapp/models-custom/report-schedule-setting';
import type ReportScheduleSettingRecord from 'webapp/models/report-schedule-setting';

import ModelManagerForStoreService from 'webapp/services/model-manager/for-store';
import type { TrackingOriginTypeForSchedulePopup } from 'webapp/services/model-manager/reports';

export default class ModelManagerReportScheduleSettingsService extends ModelManagerForStoreService<
	ReportScheduleSettingRecord,
	ReportScheduleSetting
> {
	@service('request') declare requestService: Service.Request;
	@service('popup') declare popupService: Service.Popup;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('account') declare accountService: Service.Account;
	@service('model-manager/notifications')
	declare mmNotificationsService: Service.ModelManager.Notifications;

	getAllModels() {
		return this.createModelsFromRecordsArray(this.savedRecords);
	}

	getForReport(reportId: number): ReportScheduleSetting[] {
		const records = this.savedRecords.filter((item) => item.reportId === reportId);
		return this.createModelsFromRecordsArray(records);
	}

	getAutomatedForReport(reportId: number): ReportScheduleSetting[] {
		const models = this.getForReport(reportId);
		return models.filter((item) => !item.isOneOff);
	}

	createModelForReport(report: Report) {
		const model = this.createNewModel({
			reportId: report.numericIdForSure,
		});
		return model;
	}

	createAndEditForReport(
		report: Report,
		origin: TrackingOriginTypeForSchedulePopup,
		isFromCreateChoicePopupFlow = false,
	) {
		const model = this.createModelForReport(report);
		this.edit(model, origin, isFromCreateChoicePopupFlow);
	}

	createNewAndOpenEdit(trackingOrigin: TrackingOriginTypeForSchedulePopup) {
		if (this.accountService.handleDemo()) {
			return;
		}

		const model = this.createNewModel();

		this.edit(model, trackingOrigin);
	}

	edit(
		model: ReportScheduleSetting,
		trackingOrigin: TrackingOriginTypeForSchedulePopup,
		isFromCreateChoicePopupFlow = false,
	) {
		if (this.accountService.handleDemo()) {
			return;
		}

		this.mmNotificationsService.openEditPopup(
			model,
			trackingOrigin,
			isFromCreateChoicePopupFlow,
		);
	}

	createDuplicatedModel(model: ReportScheduleSetting) {
		const data = model.getDataForClone();
		return this.createNewModel(data);
	}

	delete(scheduleSetting: ReportScheduleSetting): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		const affirmativeFunc = () => {
			const layer = this.infolayerService.loading('Deleting the schedule...');
			scheduleSetting
				.delete()
				.then(
					() => {
						layer.ok('Schedule deleted!');
					},
					(errorData) => {
						layer.close();
						throw errorData;
					},
				)
				.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
		};

		if (scheduleSetting.isAutomated) {
			const recipientTexts = getSourcesTexts(
				scheduleSetting.recipients.map((email) => {
					return { label: email };
				}),
				3,
			);
			let text = `You are about to delete a schedule to periodically deliver '${scheduleSetting.report?.title}' to `;
			text += `${recipientTexts.first}${recipientTexts.other} `;
			text += `${scheduleSetting.schedule.typeText} ${scheduleSetting.schedule.timeText}`;

			this.popupService.openConfirmPopup({
				onConfirm: affirmativeFunc,
				title: `Are you sure you want to delete the automation for the report "${scheduleSetting.report?.title}"?`,
				text,
				confirmAction: 'Delete',
				cancelAction: 'Cancel',
			});
		} else {
			affirmativeFunc();
		}
	}

	// ------ Requests ------

	_reportSchedulesRequested = false;
	_reportSchedulesRequestPromise: Promise<void> | null = null;
	requestReportSchedules(): Promise<void> {
		const variablePrefix = 'reportSchedules';
		return this.requestService.dataRequestOnce<typeof variablePrefix>(
			this,
			variablePrefix,
			() => {
				// const testData = window.dbx_mock['GET /d/report-schedules'];
				// return Promise.resolve(testData);

				return this.requestService.storeFindAll(ModelName.REPORT_SCHEDULE_SETTING);
			},
		);
	}

	// ------ For-store ------

	modelName = ModelName.REPORT_SCHEDULE_SETTING;

	createModel(model: ReportScheduleSettingRecord): ReportScheduleSetting {
		return new ReportScheduleSetting({ model });
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/report-schedule-settings': ModelManagerReportScheduleSettingsService;
	}
}
